import { extractTokenFromHash } from './invitation-token';

/**
 * Captures the magic-link invitation token out of `location.hash` and strips it
 * from the address bar BEFORE any telemetry can observe it.
 *
 * Why this is a side-effecting module rather than a line in the container
 * component: `Sentry.init` runs at `app.config.ts` module-evaluation time, which
 * is strictly earlier than any component lifecycle hook. With
 * `replaysSessionSampleRate: 1.0`, Session Replay records the initial URL —
 * fragment included — as soon as it initialises, and the history instrumentation
 * fires its `navigation` breadcrumb handler BEFORE the original
 * `history.replaceState` runs, so the pre-strip URL is captured there too. Either
 * path ships a live bearer capability, good for confirming or rejecting a real
 * order for up to 30 days, to a third-party service.
 *
 * ES module evaluation follows import order, so importing this module first in
 * `main.ts` — ahead of `app.config` — is what makes the strip win the race. Keep
 * that import first.
 *
 * ADR 0035 states the token is delivered in the fragment precisely so it never
 * reaches server logs, proxies, or Referer headers; telemetry is the same class
 * of leak and is closed here.
 */

const PUBLIC_ORDER_PATH = '/p/order';

let capturedToken: string | null = null;

/**
 * Returns the token captured at bootstrap and CLEARS it, so a later in-tab
 * hashchange (a second magic link pasted into the same tab) falls through to
 * reading the fresh fragment instead of replaying the first token.
 */
export function takeCapturedInvitationToken(): string | null {
  const token = capturedToken;
  capturedToken = null;
  return token;
}

/** The parts of `location` this needs — `window.location` is not stubbable in a browser test. */
export interface CaptureLocation {
  pathname: string;
  hash: string;
  search: string;
}

/** The part of `history` this needs. */
export interface CaptureHistory {
  replaceState(data: unknown, unused: string, url: string): void;
}

/**
 * Reads and strips the fragment when the given URL is the public order page.
 * The dependencies are parameters rather than direct `window` reads because
 * `location`'s properties are non-configurable, so a test cannot stub them.
 * The module-level call below supplies the real ones at import time.
 */
export function captureInvitationTokenFromHash(loc: CaptureLocation, history: CaptureHistory): string | null {
  if (!loc.pathname.startsWith(PUBLIC_ORDER_PATH)) {
    return null;
  }

  const token = extractTokenFromHash(loc.hash);
  if (!token) {
    return null;
  }

  capturedToken = token;
  history.replaceState(null, '', loc.pathname + loc.search);
  return token;
}

if (typeof window !== 'undefined') {
  captureInvitationTokenFromHash(window.location, window.history);
}
